import { useState } from 'react';
import { FlexColCenHorVer, FlexCol, Flex } from '@/UI/styles';
import { useResolvedAssets, useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { shareLinkUrl } from '@/utils/shareLinks';
import DotsMenu from './DotsMenu';
import DownloadMenu from './DownloadMenu';
import CopyBtnV2 from './CopyBtnV2';
import { isMediaAsset, isRenderingAsset } from './utils';
import { GalleryInfoContainer } from './GalleryInfoContainer';
import type { ImageActions } from './useImageActions';
import type { DeleteFunctions, ImageListContainerType } from './types';
import type { ImageListState } from './useImageList';

export const ImageListWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <FlexCol
      sx={{
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
        position: 'relative',
      }}
    >
      {children}
    </FlexCol>
  );
};

export const ImageListContainer = ({
  children,
  fullHeight = false,
}: {
  children: React.ReactNode;
  fullHeight: boolean;
}) => {
  return (
    <FlexColCenHorVer
      data-testid="image-list-container"
      sx={{
        height: fullHeight ? '100%' : '85%',
        width: '100%',
        flex: 1,
        minHeight: 0,
      }}
    >
      {children}
    </FlexColCenHorVer>
  );
};

type ImageListHeaderProps = Pick<ImageListState, 'isSelectedFileText' | 'selectedFile'> &
  Pick<ImageActions, 'handleCopyImage' | 'handleDownload' | 'handleDownloadAll'> & {
    container: ImageListContainerType;
    deletionFunctions?: DeleteFunctions;
    selected: number;
    zoomPercentage?: number;
  };

export const ImageListHeader = ({
  container,
  deletionFunctions,
  handleCopyImage,
  handleDownload,
  handleDownloadAll,
  isSelectedFileText,
  selected,
  selectedFile,
  zoomPercentage,
}: ImageListHeaderProps) => {
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteMenuAnchorEl, setDeleteMenuAnchorEl] = useState<HTMLElement | null>(null);

  const workflowRole = useUserWorkflowRole();
  const isLatestVersion = useWorkflowStore((s) => s.isLatestVersion);
  const resolvedAssets = useResolvedAssets();

  return (
    <Flex
      sx={{
        flexDirection: 'row-reverse',
        height: '60px',
        width: '100%',
        alignItems: 'center',
        px: 1,
      }}
    >
      {container === 'design-app' && (
        <GalleryInfoContainer selectedFile={selectedFile} zoomPercentage={zoomPercentage} />
      )}
      {isLatestVersion &&
      (workflowRole === 'editor' || container === 'design-app') &&
      selectedFile &&
      deletionFunctions ? (
        <DotsMenu
          disabled={isRenderingAsset(selectedFile)}
          setDeleteMenuAnchorEl={setDeleteMenuAnchorEl}
          deleteMenuAnchorEl={deleteMenuAnchorEl}
          deletionFunctions={deletionFunctions}
        />
      ) : null}
      {isSelectedFileText ? null : (
        <DownloadMenu
          selected={selected}
          setDownloadMenuAnchorEl={setDownloadMenuAnchorEl}
          downloadMenuAnchorEl={downloadMenuAnchorEl}
          handleDownload={handleDownload}
          handleDownloadAll={handleDownloadAll}
          handleCopyImage={handleCopyImage}
          disabled={isRenderingAsset(selectedFile)}
          isAddCopyBtn={selectedFile?.type === 'image'}
        />
      )}
      <CopyBtnV2
        disabled={!isMediaAsset(selectedFile)}
        eventTracking={{ name: 'node_image_copy_link_clicked', payload: {} }}
        text={isMediaAsset(selectedFile) ? shareLinkUrl(selectedFile, resolvedAssets) : ''}
      />
    </Flex>
  );
};
